import type { CreatePublicationRequest } from "@led-signage/shared/management-api";

/**
 * 緊急配信の申請フォームの純粋ロジック。
 *
 * 規則は[承認・公開仕様](../../docs/specifications/approval-and-publishing.md#緊急配信)に従う。
 * 理由・対象LED・終了条件が必須で、終了時刻は将来でなければならない。
 */

export interface EmergencyFormState {
  scheduleRevisionId: string;
  displayIds: readonly string[];
  reason: string;
  /** `<input type="datetime-local">` の値。端末のローカル時刻。 */
  endsAtLocal: string;
  acknowledged: boolean;
}

export function emptyEmergencyForm(): EmergencyFormState {
  return {
    scheduleRevisionId: "",
    displayIds: [],
    reason: "",
    endsAtLocal: "",
    acknowledged: false,
  };
}

/**
 * `datetime-local` の値をUTCのRFC 3339へ。
 *
 * 入力はタイムゾーンを持たないため、ブラウザのローカル時刻として解釈する。
 * `Date` のコンストラクタに任せると環境差が出るので、明示的に組み立てる。
 */
export function toUtcTimestamp(localValue: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(localValue);
  if (match === null) return null;
  const [, year, month, day, hour, minute] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export interface EmergencyIssue {
  field: keyof EmergencyFormState;
  message: string;
}

export function inspectEmergency(
  form: EmergencyFormState,
  now: Date,
): EmergencyIssue[] {
  const issues: EmergencyIssue[] = [];

  if (form.scheduleRevisionId.length === 0) {
    issues.push({
      field: "scheduleRevisionId",
      message: "緊急配信する編成を選んでください。",
    });
  }
  if (form.displayIds.length === 0) {
    issues.push({
      field: "displayIds",
      message: "対象のLEDを1つ以上選んでください。",
    });
  }
  if (form.reason.trim().length === 0) {
    issues.push({
      field: "reason",
      message: "理由は必須です。監査ログへ記録されます。",
    });
  }

  const endsAt = toUtcTimestamp(form.endsAtLocal);
  if (endsAt === null) {
    issues.push({
      field: "endsAtLocal",
      message: "自動終了日時を入力してください。",
    });
  } else if (Date.parse(endsAt) <= now.getTime()) {
    // 過去の終了時刻を許すと、開始した瞬間に終わる編成を作れてしまう。
    issues.push({
      field: "endsAtLocal",
      message: "自動終了日時は現在より後にしてください。",
    });
  }

  if (!form.acknowledged) {
    issues.push({
      field: "acknowledged",
      message: "通常放映が中断されることの確認が必要です。",
    });
  }

  return issues;
}

export function toEmergencyRequest(
  form: EmergencyFormState,
): CreatePublicationRequest {
  const endsAt = toUtcTimestamp(form.endsAtLocal);
  if (endsAt === null) throw new Error("invalid emergency end time");
  return {
    scheduleRevisionIds: [form.scheduleRevisionId],
    displayIds: form.displayIds,
    changeReason: form.reason.trim(),
    // 緊急配信は当日変更の確認とは別の経路で扱う。
    sameDayChange: false,
    emergencyOverride: true,
    emergencyEndsAt: endsAt,
  };
}
